import { useEffect, useMemo, useState } from 'react';
import { useToast } from '../components/Toast';
import CancelMeetingModal from '../components/CancelMeetingModal';
import { getAllMembers } from '../lib/data';
import { apiClient } from '../lib/api/client';
import { getSession, isAdmin } from '../lib/auth';
import {
  cancelMeeting,
  getActiveHostSession,
  getMeetingSchedule,
  reinstateMeeting,
  rescheduleGuestInvite,
  revokeHostSession,
} from '../lib/meetings';
import {
  listGuestInvitesExec,
  recordGuestConflictDecision,
  sendGuestInviteExec,
} from '../lib/guestInvites';
import {
  promoteExecutiveProspect,
  updateExecutiveMemberProfile,
  updateExecutiveProspectRecord,
} from '../lib/executiveRecords';
import {
  EMPTY_MEETING_WELCOME,
  getMeetingWelcome,
  normalizeMeetingWelcome,
  updateMeetingWelcome,
} from '../lib/meetingWelcome';

const CONFLICT_LABELS = {
  pending: 'Review pending',
  no_conflict: 'No Conflict',
  conflict: 'Conflict',
};

const CONFLICT_PILLS = {
  pending: 'warn',
  no_conflict: 'ok',
  conflict: 'bad',
};

const PAYMENT_LABELS = {
  not_started: 'Not started',
  pending: 'Awaiting payment',
  paid: 'Paid',
  failed: 'Failed',
};

const PAYMENT_PILLS = {
  not_started: 'neutral',
  pending: 'warn',
  paid: 'ok',
  failed: 'bad',
};

const PAYER_LABELS = {
  member: 'Inviting member',
  guest: 'Guest',
};

const PILL_CLASSES = {
  ok: 'bg-green-100 text-green-900',
  warn: 'bg-amber-100 text-amber-900',
  bad: 'bg-red-100 text-red-900',
  neutral: 'bg-gray-100 text-gray-800',
};

function StatusPill({ kind = 'neutral', children }) {
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${PILL_CLASSES[kind]}`}>
      {children}
    </span>
  );
}

// Directory/profile fields an Executive or Admin may edit for an Executive or
// Member record. These map 1:1 onto PATCH /api/executive/members/{id}/profile.
const MEMBER_EDIT_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'businessName', label: 'Business name' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'website', label: 'Website' },
  { key: 'shortDescription', label: 'Short description' },
];

// Contact/application fields shown for a Prospect. These map onto
// PATCH /api/executive/prospects/{id}, which also mirrors the shared
// identity/contact fields onto the linked canonical member row.
const PROSPECT_EDIT_FIELDS = [
  { key: 'fullName', label: 'Full name' },
  { key: 'businessName', label: 'Business name' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
];

const MEMBER_DESIGNATIONS = ['Executive', 'Member', 'Substitute', 'Former Member'];

function designationForMember(member) {
  const status = String(member?.status ?? '').trim().toLowerCase();
  const role = String(member?.role ?? '').trim().toLowerCase();
  const memberType = String(member?.memberType ?? member?.member_type ?? '').trim().toLowerCase();
  if (status === 'inactive') return 'Former Member';
  if (role === 'executive' || role === 'exec') return 'Executive';
  if (memberType === 'sub') return 'Substitute';
  return 'Member';
}

function RecordEditForm({
  idPrefix,
  fields,
  values,
  onChange,
  onSave,
  onCancel,
  isSaving,
  showDesignation = false,
}) {
  return (
    <form
      className="mt-2 space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3"
      data-testid="record-edit-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      {showDesignation ? (
        <div>
          <label className="block text-xs text-gray-600" htmlFor={`${idPrefix}-designation`}>
            Designation
          </label>
          <select
            id={`${idPrefix}-designation`}
            name="designation"
            data-testid="record-edit-designation"
            className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
            value={values?.designation ?? 'Member'}
            onChange={(e) => onChange('designation', e.target.value)}
            disabled={isSaving}
          >
            {MEMBER_DESIGNATIONS.map((designation) => (
              <option key={designation} value={designation}>{designation}</option>
            ))}
          </select>
        </div>
      ) : null}

      {fields.map((field) => (
        <div key={field.key}>
          <label className="block text-xs text-gray-600" htmlFor={`${idPrefix}-${field.key}`}>
            {field.label}
          </label>
          <input
            id={`${idPrefix}-${field.key}`}
            name={field.key}
            data-testid={`record-edit-${field.key}`}
            className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
            value={values?.[field.key] ?? ''}
            onChange={(e) => onChange(field.key, e.target.value)}
            disabled={isSaving}
          />
        </div>
      ))}

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          className="rounded-md bg-NBNA-primary px-3 py-1.5 text-sm text-white disabled:opacity-50"
          data-testid="record-edit-save"
          disabled={isSaving}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 disabled:opacity-50"
          data-testid="record-edit-cancel"
          disabled={isSaving}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function isToday(value) {
  if (!value) return false;
  const created = new Date(value);
  const now = new Date();
  return (
    created.getFullYear() === now.getFullYear()
    && created.getMonth() === now.getMonth()
    && created.getDate() === now.getDate()
  );
}

export default function Executive() {
  const { showToast } = useToast();
  const [members, setMembers] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [guestInvites, setGuestInvites] = useState([]);
  const [guestInviteMeeting, setGuestInviteMeeting] = useState(null);
  const [busyInviteId, setBusyInviteId] = useState('');
  const [schedule, setSchedule] = useState([]);
  const [busyMeetingId, setBusyMeetingId] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [meetingIsOpen, setMeetingIsOpen] = useState(false);
  const [isRevokingHost, setIsRevokingHost] = useState(false);

  const [promoteMemberId, setPromoteMemberId] = useState('');
  const [assignMemberId, setAssignMemberId] = useState('');
  const [assignBranchIds, setAssignBranchIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Inline record editing for the Executives / Members / Prospects lists.
  // { kind: 'member' | 'prospect', id, values }
  const [editingRecord, setEditingRecord] = useState(null);
  const [isSavingRecord, setIsSavingRecord] = useState(false);
  const [promotingProspectId, setPromotingProspectId] = useState('');

  const [canManageBranches, setCanManageBranches] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [isCreatingBranch, setIsCreatingBranch] = useState(false);

  const [industriesSought, setIndustriesSought] = useState([]);
  const [newIndustry, setNewIndustry] = useState('');
  const [isSavingIndustries, setIsSavingIndustries] = useState(false);
  const [canManageIndustries, setCanManageIndustries] = useState(false);
  const [meetingWelcome, setMeetingWelcome] = useState(EMPTY_MEETING_WELCOME);
  const [isSavingMeetingWelcome, setIsSavingMeetingWelcome] = useState(false);

  const executives = useMemo(() => {
    return (members ?? []).filter((m) => String(m?.role ?? '').toLowerCase() === 'executive');
  }, [members]);

  const regularMembers = useMemo(() => {
    return (members ?? []).filter((m) => {
      const role = String(m?.role ?? '').toLowerCase();
      const memberType = String(m?.memberType ?? m?.member_type ?? '').toLowerCase();
      return (role === 'member' || role === '') && memberType !== 'sub';
    });
  }, [members]);

  const substitutes = useMemo(() => {
    return (members ?? []).filter((m) => (
      String(m?.memberType ?? m?.member_type ?? '').toLowerCase() === 'sub'
    ));
  }, [members]);

  async function refresh() {
    setIsLoading(true);
    try {
      const session = await getSession();
      setCanManageBranches(Boolean(session && isAdmin(session)));
      const isExec =
        session &&
        (isAdmin(session) ||
          session?.user?.backendRole === 'Executive' ||
          session?.user?.role === 'Executive' ||
          session?.user?.uiRole === 'executive');
      setCanManageIndustries(Boolean(isExec));

      const [{ members: m }] = await Promise.all([getAllMembers({ offset: 0, limit: 100 })]);
      setMembers(m ?? []);

      const [p, b, industriesResp, welcomeResp, hostSessionResp, guestInvitesResp, scheduleResp] = await Promise.all([
        apiClient.get('/api/prospects', { params: { offset: 0, limit: 200 } }),
        apiClient.get('/api/executive-branches'),
        apiClient.get('/api/public/industries-sought'),
        getMeetingWelcome(),
        getActiveHostSession().catch(() => null),
        // No meeting scheduled is a normal state, not a dashboard failure.
        listGuestInvitesExec().catch(() => null),
        getMeetingSchedule().catch(() => null),
      ]);

      setMeetingIsOpen(Boolean(hostSessionResp?.hostSession?.id));

      setProspects(p?.prospects ?? []);
      setGuestInvites(
        (guestInvitesResp?.invites ?? []).map((inv) => ({
          ...inv,
          registeredToday: isToday(inv.createdAt),
        })),
      );
      setGuestInviteMeeting(guestInvitesResp?.meeting ?? null);
      setSchedule(scheduleResp?.meetings ?? []);
      setBranches(b?.branches ?? b ?? []);
      setIndustriesSought(Array.isArray(industriesResp?.industries) ? industriesResp.industries : []);
      setMeetingWelcome(normalizeMeetingWelcome(welcomeResp));
    } catch (e) {
      const msg = e?.message ?? 'Failed to load executive dashboard.';
      showToast(msg, { kind: 'error' });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    refresh();  }, []);

  async function onConfirmCancel(options) {
    const meeting = cancelTarget;
    if (!meeting) return;
    setBusyMeetingId(meeting.id);
    try {
      const result = await cancelMeeting(meeting.id, options);
      const moved = result?.moved ?? 0;
      const notified = result?.notified ?? 0;
      showToast(
        moved > 0
          ? `Meeting cancelled. ${moved} registration${moved === 1 ? '' : 's'} moved and ${notified} guest${notified === 1 ? '' : 's'} emailed.`
          : `Meeting cancelled. ${notified} guest${notified === 1 ? '' : 's'} emailed with a link to choose another morning.`,
        { kind: 'success' },
      );
      const stuck = result?.alreadyRegisteredElsewhere ?? [];
      if (stuck.length) {
        showToast(
          `Already registered for the new date, so left as they were: ${stuck.join(', ')}.`,
          { kind: 'info' },
        );
      }
      setCancelTarget(null);
      await refresh();
    } catch (e) {
      showToast(e?.message ?? 'Failed to cancel the meeting.', { kind: 'error' });
    } finally {
      setBusyMeetingId('');
    }
  }

  async function onMoveGuest(invite, meetingId) {
    if (!meetingId || busyInviteId) return;
    setBusyInviteId(invite.id);
    try {
      await rescheduleGuestInvite(invite.id, meetingId);
      showToast('Guest moved, and emailed the new date.', { kind: 'success' });
      await refresh();
    } catch (e) {
      showToast(e?.message ?? 'Failed to move the guest.', { kind: 'error' });
    } finally {
      setBusyInviteId('');
    }
  }

  async function onReinstateMeeting(meeting) {
    if (busyMeetingId) return;
    setBusyMeetingId(meeting.id);
    try {
      await reinstateMeeting(meeting.id);
      showToast('Meeting reinstated. Registration is open again.', { kind: 'success' });
      await refresh();
    } catch (e) {
      showToast(e?.message ?? 'Failed to reinstate the meeting.', { kind: 'error' });
    } finally {
      setBusyMeetingId('');
    }
  }

  async function onRecordConflict(invite, decision) {
    if (busyInviteId) return;
    setBusyInviteId(invite.id);
    try {
      await recordGuestConflictDecision(invite.id, decision);
      showToast(
        decision === 'no_conflict'
          ? 'No Conflict recorded. You can now send the welcome and request the fee.'
          : 'Conflict recorded. This registration stays pending for Leadership.',
        { kind: 'success' },
      );
      await refresh();
    } catch (e) {
      showToast(e?.message ?? 'Failed to record the conflict decision.', { kind: 'error' });
    } finally {
      setBusyInviteId('');
    }
  }

  async function onRequestGuestFee(invite) {
    if (busyInviteId) return;
    setBusyInviteId(invite.id);
    try {
      await sendGuestInviteExec(invite.id);
      showToast('Welcome sent and the $30 fee requested.', { kind: 'success' });
      await refresh();
    } catch (e) {
      showToast(e?.message ?? 'Failed to send the welcome and fee request.', { kind: 'error' });
    } finally {
      setBusyInviteId('');
    }
  }

  function startEditMember(member) {
    setEditingRecord({
      kind: 'member',
      id: String(member.id),
      values: {
        ...Object.fromEntries(
          MEMBER_EDIT_FIELDS.map((field) => [field.key, String(member?.[field.key] ?? '')]),
        ),
        designation: designationForMember(member),
      },
    });
  }

  function startEditProspect(prospect) {
    setEditingRecord({
      kind: 'prospect',
      id: String(prospect.id),
      values: Object.fromEntries(
        PROSPECT_EDIT_FIELDS.map((field) => [field.key, String(prospect?.[field.key] ?? '')]),
      ),
    });
  }

  function onEditFieldChange(key, value) {
    setEditingRecord((prev) => (prev ? { ...prev, values: { ...prev.values, [key]: value } } : prev));
  }

  // Cancel is intentionally local-only: it drops the draft without any request,
  // so the record on the server is untouched.
  function onCancelEdit() {
    setEditingRecord(null);
  }

  async function onSaveRecord() {
    if (!editingRecord || isSavingRecord) return;
    setIsSavingRecord(true);
    try {
      if (editingRecord.kind === 'prospect') {
        await updateExecutiveProspectRecord(editingRecord.id, editingRecord.values);
        showToast('Prospect record updated.', { kind: 'success' });
      } else {
        await updateExecutiveMemberProfile(editingRecord.id, editingRecord.values);
        showToast('Member record updated.', { kind: 'success' });
      }
      setEditingRecord(null);
      await refresh();
    } catch (e) {
      showToast(e?.message ?? 'Failed to save the record.', { kind: 'error' });
    } finally {
      setIsSavingRecord(false);
    }
  }

  async function onPromoteProspect(prospect) {
    if (!prospect?.id || promotingProspectId) return;
    setPromotingProspectId(String(prospect.id));
    try {
      await promoteExecutiveProspect(prospect.id);
      showToast('Prospect promoted to Member.', { kind: 'success' });
      await refresh();
    } catch (e) {
      showToast(e?.message ?? 'Failed to promote prospect.', { kind: 'error' });
    } finally {
      setPromotingProspectId('');
    }
  }

  async function onPromote() {
    if (!promoteMemberId) return;
    setIsSaving(true);
    try {
      await apiClient.patch(`/api/executive/members/${promoteMemberId}/role`, { role: 'executive' });
      showToast('Member promoted to Executive.', { kind: 'success' });
      await refresh();
    } catch (e) {
      showToast(e?.message ?? 'Failed to promote member.', { kind: 'error' });
    } finally {
      setIsSaving(false);
    }
  }

  async function onAssignBranches() {
    if (!assignMemberId) return;
    setIsSaving(true);
    try {
      await apiClient.patch(`/api/executive/members/${assignMemberId}/executive-branches`, {
        branchIds: assignBranchIds,
      });
      showToast('Executive branches updated.', { kind: 'success' });
    } catch (e) {
      showToast(e?.message ?? 'Failed to update executive branches.', { kind: 'error' });
    } finally {
      setIsSaving(false);
    }
  }

  async function onCreateBranch() {
    const name = String(newBranchName ?? '').trim();
    if (!name) {
      showToast('Branch name is required.', { kind: 'error' });
      return;
    }

    setIsCreatingBranch(true);
    try {
      await apiClient.post('/api/admin/executive-branches', { name });
      setNewBranchName('');
      showToast('Executive branch created.', { kind: 'success' });
      await refresh();
    } catch (e) {
      showToast(e?.message ?? 'Failed to create executive branch.', { kind: 'error' });
    } finally {
      setIsCreatingBranch(false);
    }
  }

  async function onAddIndustry() {
    const next = String(newIndustry ?? '').trim();
    if (!next) return;

    const merged = Array.from(
      new Set([...(industriesSought ?? []).map((x) => String(x).trim()).filter(Boolean), next])
    );

    setIsSavingIndustries(true);
    try {
      await apiClient.patch('/api/executive/industries-sought', {
        industries: merged,
      });
      setNewIndustry('');
      setIndustriesSought(merged);
      showToast('Industries sought updated.', { kind: 'success' });
    } catch (e) {
      showToast(e?.message ?? 'Failed to update industries sought.', { kind: 'error' });
    } finally {
      setIsSavingIndustries(false);
    }
  }

  async function onResetIndustries() {
    setIsSavingIndustries(true);
    try {
      await apiClient.patch('/api/executive/industries-sought', {
        industries: [],
      });
      setIndustriesSought([]);
      showToast('Industries sought reset.', { kind: 'success' });
    } catch (e) {
      showToast(e?.message ?? 'Failed to reset industries sought.', { kind: 'error' });
    } finally {
      setIsSavingIndustries(false);
    }
  }

  async function onSaveMeetingWelcome() {
    if (isSavingMeetingWelcome) return;
    setIsSavingMeetingWelcome(true);
    try {
      const saved = await updateMeetingWelcome(meetingWelcome);
      setMeetingWelcome(normalizeMeetingWelcome(saved));
      showToast('Meeting welcome screen updated.', { kind: 'success' });
    } catch (e) {
      showToast(e?.message ?? 'Failed to update the meeting welcome screen.', { kind: 'error' });
    } finally {
      setIsSavingMeetingWelcome(false);
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-NBNA-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-semibold text-NBNA-primary">Executive Dashboard</h1>

      <section className="mt-6 bg-white shadow rounded-lg p-6" data-testid="meeting-welcome-settings">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-NBNA-primary">Meeting Welcome Screen</h2>
            <p className="mt-1 text-sm text-gray-600">
              Update the prior week&apos;s figures shown beside the permanent meeting registration QR code.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a className="btn-secondary" href="/meeting/welcome" target="_blank" rel="noreferrer">
              Open welcome screen
            </a>
            <a className="btn-secondary" href="/downloads/NBNA-Meeting-Welcome-2026-09-01.pptx" download>
              Download PowerPoint
            </a>
          </div>
        </div>

        <form
          className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSaveMeetingWelcome();
          }}
        >
          {[
            ['reportDate', 'Report date', 'date', ''],
            ['closedBusiness', 'Thank you for closed business', 'number', '0.01'],
            ['yearToDate', 'Total for 2026 to date', 'number', '0.01'],
            ['onTrack', 'On track for 2026', 'number', '0.01'],
            ['dailyAverage', 'Current daily average', 'number', '0.01'],
            ['memberCount', 'Member count', 'number', '1'],
            ['averageSeatValue', 'Average seat value', 'number', '0.01'],
          ].map(([key, label, type, step]) => (
            <label key={key} className="block text-sm font-medium text-gray-700">
              {label}
              <input
                type={type}
                step={step || undefined}
                min={key === 'memberCount' ? '1' : '0'}
                required
                data-testid={`meeting-welcome-${key}`}
                className="mt-1 w-full rounded-md border border-gray-300 p-2 text-gray-900"
                value={meetingWelcome[key]}
                disabled={isSavingMeetingWelcome}
                onChange={(e) => setMeetingWelcome((previous) => ({
                  ...previous,
                  [key]: e.target.value,
                }))}
              />
            </label>
          ))}
          <div className="flex items-end">
            <button
              type="submit"
              className="btn-primary w-full disabled:opacity-50"
              data-testid="meeting-welcome-save"
              disabled={isSavingMeetingWelcome}
            >
              {isSavingMeetingWelcome ? 'Saving…' : 'Save meeting screen'}
            </button>
          </div>
        </form>
      </section>

      {meetingIsOpen ? (
        <section className="mt-6 bg-white shadow rounded-lg p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-NBNA-primary">Meeting Host Mode</h2>
              <p className="mt-1 text-sm text-gray-600">
                Executives can revoke an active host session if the host needs to leave or the device was hijacked.
              </p>
            </div>

            <button
              type="button"
              className="btn-secondary"
              disabled={isRevokingHost}
              onClick={async () => {
                if (isRevokingHost) return;
                setIsRevokingHost(true);
                try {
                  await revokeHostSession();
                  showToast('Host mode revoked. Anyone can reclaim it from the dashboard.', { kind: 'success' });
                  setMeetingIsOpen(false);
                } catch (e) {
                  showToast(e?.message ?? 'Failed to revoke host mode.', { kind: 'error' });
                } finally {
                  setIsRevokingHost(false);
                }
              }}
            >
              {isRevokingHost ? 'Revoking…' : 'Revoke Active Host Session'}
            </button>
          </div>
        </section>
      ) : null}

      <section className="mt-6 bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-NBNA-primary">Industries Sought</h2>
        <ul className="mt-3 list-disc pl-6 text-gray-800">
          {industriesSought.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-NBNA-primary">Executives</h2>
          <ul className="mt-3 space-y-3">
            {executives.map((m) => (
              <li
                key={m.id}
                className="text-gray-800"
                data-testid="executives-record-row"
                data-record-id={m.id}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium" data-testid="record-name">{m.name ?? ''}</div>
                    <div className="text-sm text-gray-600" data-testid="record-business">
                      {m.businessName ?? ''}
                    </div>
                    <div className="text-sm text-gray-600" data-testid="record-phone">
                      {m.phone ?? ''}
                    </div>
                    <div className="text-sm text-gray-600" data-testid="record-email">
                      {m.email ?? ''}
                    </div>
                    <div className="text-xs font-medium text-gray-700" data-testid="record-designation">
                      {designationForMember(m)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-800 disabled:opacity-50"
                    data-testid="record-edit"
                    disabled={isSavingRecord}
                    onClick={() => startEditMember(m)}
                  >
                    Edit
                  </button>
                </div>

                {editingRecord?.kind === 'member' && editingRecord.id === String(m.id) ? (
                  <RecordEditForm
                    idPrefix={`exec-record-${m.id}`}
                    fields={MEMBER_EDIT_FIELDS}
                    values={editingRecord.values}
                    onChange={onEditFieldChange}
                    onSave={onSaveRecord}
                    onCancel={onCancelEdit}
                    isSaving={isSavingRecord}
                    showDesignation
                  />
                ) : null}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-NBNA-primary">Substitutes</h2>
          <ul className="mt-3 space-y-3">
            {substitutes.map((m) => (
              <li
                key={m.id}
                className="text-gray-800"
                data-testid="substitutes-record-row"
                data-record-id={m.id}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium" data-testid="record-name">{m.name ?? ''}</div>
                    <div className="text-sm text-gray-600" data-testid="record-business">
                      {m.businessName ?? ''}
                    </div>
                    <div className="text-sm text-gray-600" data-testid="record-phone">
                      {m.phone ?? ''}
                    </div>
                    <div className="text-sm text-gray-600" data-testid="record-email">
                      {m.email ?? ''}
                    </div>
                    <div className="text-xs font-medium text-gray-700" data-testid="record-designation">
                      {designationForMember(m)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-800 disabled:opacity-50"
                    data-testid="record-edit"
                    disabled={isSavingRecord}
                    onClick={() => startEditMember(m)}
                  >
                    Edit
                  </button>
                </div>

                {editingRecord?.kind === 'member' && editingRecord.id === String(m.id) ? (
                  <RecordEditForm
                    idPrefix={`substitute-record-${m.id}`}
                    fields={MEMBER_EDIT_FIELDS}
                    values={editingRecord.values}
                    onChange={onEditFieldChange}
                    onSave={onSaveRecord}
                    onCancel={onCancelEdit}
                    isSaving={isSavingRecord}
                    showDesignation
                  />
                ) : null}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-NBNA-primary">Members</h2>
          <ul className="mt-3 space-y-3">
            {regularMembers.map((m) => (
              <li
                key={m.id}
                className="text-gray-800"
                data-testid="members-record-row"
                data-record-id={m.id}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium" data-testid="record-name">{m.name ?? ''}</div>
                    <div className="text-sm text-gray-600" data-testid="record-business">
                      {m.businessName ?? ''}
                    </div>
                    <div className="text-sm text-gray-600" data-testid="record-phone">
                      {m.phone ?? ''}
                    </div>
                    <div className="text-sm text-gray-600" data-testid="record-email">
                      {m.email ?? ''}
                    </div>
                    <div className="text-xs font-medium text-gray-700" data-testid="record-designation">
                      {designationForMember(m)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-800 disabled:opacity-50"
                    data-testid="record-edit"
                    disabled={isSavingRecord}
                    onClick={() => startEditMember(m)}
                  >
                    Edit
                  </button>
                </div>

                {editingRecord?.kind === 'member' && editingRecord.id === String(m.id) ? (
                  <RecordEditForm
                    idPrefix={`member-record-${m.id}`}
                    fields={MEMBER_EDIT_FIELDS}
                    values={editingRecord.values}
                    onChange={onEditFieldChange}
                    onSave={onSaveRecord}
                    onCancel={onCancelEdit}
                    isSaving={isSavingRecord}
                    showDesignation
                  />
                ) : null}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-NBNA-primary">Prospects</h2>
          <ul className="mt-3 space-y-3">
            {(prospects ?? []).map((p) => (
              <li
                key={p.id}
                className="text-gray-800"
                data-testid="prospects-record-row"
                data-record-id={p.id}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium" data-testid="record-name">{p.fullName ?? ''}</div>
                    <div className="text-sm text-gray-600" data-testid="record-business">
                      {p.businessName ?? ''}
                    </div>
                    <div className="text-sm text-gray-600" data-testid="record-phone">
                      {p.phone ?? ''}
                    </div>
                    <div className="text-sm text-gray-600" data-testid="record-email">
                      {p.email ?? ''}
                    </div>
                    <div className="text-xs font-medium text-gray-700" data-testid="record-designation">
                      Prospect
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-800 disabled:opacity-50"
                      data-testid="record-edit"
                      disabled={isSavingRecord || Boolean(promotingProspectId)}
                      onClick={() => startEditProspect(p)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="rounded-md bg-NBNA-primary px-2 py-1 text-sm text-white disabled:opacity-50"
                      data-testid="record-promote"
                      disabled={isSavingRecord || Boolean(promotingProspectId)}
                      onClick={() => onPromoteProspect(p)}
                    >
                      {promotingProspectId === String(p.id) ? 'Promoting…' : 'Promote'}
                    </button>
                  </div>
                </div>

                {editingRecord?.kind === 'prospect' && editingRecord.id === String(p.id) ? (
                  <RecordEditForm
                    idPrefix={`prospect-record-${p.id}`}
                    fields={PROSPECT_EDIT_FIELDS}
                    values={editingRecord.values}
                    onChange={onEditFieldChange}
                    onSave={onSaveRecord}
                    onCancel={onCancelEdit}
                    isSaving={isSavingRecord}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-6 bg-white shadow rounded-lg p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-NBNA-primary">Executive Branches</h2>
            <p className="mt-1 text-sm text-gray-600">Branches organize executive responsibilities.</p>
          </div>

          {canManageBranches ? (
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
              <div>
                <label className="block text-xs text-gray-600" htmlFor="new-exec-branch">
                  New branch
                </label>
                <input
                  id="new-exec-branch"
                  value={newBranchName}
                  onChange={(e) => setNewBranchName(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
                  placeholder="e.g. Education"
                  disabled={isCreatingBranch}
                />
              </div>
              <button
                type="button"
                className="btn-primary whitespace-nowrap"
                onClick={onCreateBranch}
                disabled={isCreatingBranch || !String(newBranchName ?? '').trim()}
              >
                Create
              </button>
            </div>
          ) : null}
        </div>

        <ul className="mt-4 space-y-2">
          {(branches ?? []).length ? (
            (branches ?? []).map((b) => (
              <li key={b.id ?? b.name} className="text-gray-800">{b.name ?? b.id}</li>
            ))
          ) : (
            <li className="text-sm text-gray-500">No branches yet.</li>
          )}
        </ul>
      </section>

      <section className="mt-6 bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-NBNA-primary">Executive Management</h2>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-md border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900">Promote member to Executive</h3>
            <label className="mt-3 block text-sm text-gray-700" htmlFor="promote-member">
              Member
            </label>
            <select
              id="promote-member"
              className="mt-1 w-full rounded-md border border-gray-300 p-2"
              value={promoteMemberId}
              onChange={(e) => setPromoteMemberId(e.target.value)}
              disabled={isSaving}
            >
              <option value="">Select a member…</option>
              {regularMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name ?? m.businessName ?? m.id}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="mt-3 rounded-md bg-NBNA-primary px-3 py-2 text-white disabled:opacity-50"
              onClick={onPromote}
              disabled={!promoteMemberId || isSaving}
            >
              Promote
            </button>
          </div>

          <div className="rounded-md border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900">Industries sought (public)</h3>
            <p className="mt-1 text-sm text-gray-600">
              Manage the &quot;Industries Sought&quot; list shown on the public home page.
            </p>

            <div className="mt-3">
              <div className="text-sm text-gray-700">Current industries</div>
              <ul className="mt-2 list-disc pl-6 text-sm text-gray-800 space-y-1">
                {(industriesSought ?? []).length ? (
                  (industriesSought ?? []).map((i) => <li key={i}>{i}</li>)
                ) : (
                  <li className="text-gray-500">None</li>
                )}
              </ul>
            </div>

            {canManageIndustries ? (
              <div className="mt-4 flex flex-col gap-2">
                <label className="block text-sm text-gray-700" htmlFor="new-industry">
                  Add industry
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    id="new-industry"
                    className="w-full rounded-md border border-gray-300 p-2"
                    value={newIndustry}
                    onChange={(e) => setNewIndustry(e.target.value)}
                    placeholder="e.g. Construction"
                    disabled={isSavingIndustries}
                  />
                  <button
                    type="button"
                    className="btn-primary whitespace-nowrap"
                    onClick={onAddIndustry}
                    disabled={isSavingIndustries || !String(newIndustry ?? '').trim()}
                  >
                    Add
                  </button>
                </div>

                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onResetIndustries}
                  disabled={isSavingIndustries}
                >
                  Reset industries list
                </button>
              </div>
            ) : (
              <p className="mt-4 text-sm text-gray-500">Admins can edit this list.</p>
            )}
          </div>

          <div className="rounded-md border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900">Assign executive branches</h3>
            <label className="mt-3 block text-sm text-gray-700" htmlFor="assign-member">
              Executive member
            </label>
            <select
              id="assign-member"
              className="mt-1 w-full rounded-md border border-gray-300 p-2"
              value={assignMemberId}
              onChange={(e) => setAssignMemberId(e.target.value)}
              disabled={isSaving}
            >
              <option value="">Select an executive…</option>
              {executives.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name ?? m.businessName ?? m.id}
                </option>
              ))}
            </select>

            <div className="mt-3">
              <div className="text-sm text-gray-700">Branches</div>
              <div className="mt-2 flex flex-col gap-2">
                {(branches ?? []).map((b) => {
                  const id = String(b.id ?? '');
                  const checked = assignBranchIds.includes(id);
                  return (
                    <label key={id || b.name} className="inline-flex items-center gap-2 text-sm text-gray-800">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (!id) return;
                          setAssignBranchIds((prev) => {
                            if (e.target.checked) return Array.from(new Set([...prev, id]));
                            return prev.filter((x) => x !== id);
                          });
                        }}
                        disabled={isSaving}
                      />
                      <span>{b.name ?? b.id}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              className="mt-3 rounded-md bg-NBNA-primary px-3 py-2 text-white disabled:opacity-50"
              onClick={onAssignBranches}
              disabled={!assignMemberId || isSaving}
            >
              Save branch assignments
            </button>
          </div>
        </div>
      </section>

      <section className="mt-6 bg-white shadow rounded-lg p-6" data-testid="meeting-schedule">
        <div>
          <h2 className="text-xl font-semibold text-NBNA-primary">Meeting Schedule</h2>
          <p className="mt-1 text-sm text-gray-600">
            The QR code at the door is permanent and registers whoever scans it for that
            morning&apos;s meeting. Cancelling a meeting here is what closes it.
          </p>
        </div>

        {schedule?.length ? (
          <ul className="mt-4 space-y-3">
            {schedule.map((m) => {
              const cancelled = Boolean(m.cancelledAt);
              return (
                <li
                  key={m.id}
                  className="rounded-lg border border-gray-200 p-4"
                  data-testid="meeting-row"
                  data-meeting-id={m.id}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        {new Date(m.startsAt).toLocaleString(undefined, {
                          weekday: 'long', month: 'long', day: 'numeric',
                          hour: 'numeric', minute: '2-digit',
                        })}
                        {cancelled ? (
                          <span
                            className="ml-2 rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-900"
                            data-testid="meeting-cancelled-badge"
                          >
                            Cancelled
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1 text-sm text-gray-600" data-testid="meeting-guest-counts">
                        {m.guestCount} guest{m.guestCount === 1 ? '' : 's'} registered
                        {m.paidGuestCount > 0 ? ` · ${m.paidGuestCount} paid` : ''}
                      </div>
                      {cancelled && m.cancelledByName ? (
                        <div className="mt-1 text-xs text-gray-500">
                          Cancelled by {m.cancelledByName}
                          {m.cancellationReason ? ` — ${m.cancellationReason}` : ''}
                        </div>
                      ) : null}
                    </div>

                    {cancelled ? (
                      <button
                        type="button"
                        className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="meeting-reinstate"
                        disabled={busyMeetingId === m.id}
                        onClick={() => onReinstateMeeting(m)}
                      >
                        Reinstate
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="meeting-cancel"
                        disabled={busyMeetingId === m.id}
                        onClick={() => setCancelTarget(m)}
                      >
                        Cancel meeting
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-3 text-gray-600">No meetings on the schedule.</p>
        )}
      </section>

      <CancelMeetingModal
        isOpen={Boolean(cancelTarget)}
        meeting={cancelTarget}
        alternatives={schedule}
        onClose={() => setCancelTarget(null)}
        onConfirm={onConfirmCancel}
      />

      <section className="mt-6 bg-white shadow rounded-lg p-6" data-testid="guest-review">
        <div>
          <h2 className="text-xl font-semibold text-NBNA-primary">Guest Registrations</h2>
          <p className="mt-1 text-sm text-gray-600">
            Confirm each guest&apos;s business type does not conflict with an existing member. The $30
            fee request and the guest welcome are not sent until “No Conflict” is recorded.
          </p>
          {guestInviteMeeting?.startsAt ? (
            <p className="mt-1 text-sm text-gray-600">
              Meeting: {new Date(guestInviteMeeting.startsAt).toLocaleString()}
            </p>
          ) : null}
        </div>

        {guestInvites?.length ? (
          <ul className="mt-4 space-y-3">
            {guestInvites.map((inv) => {
              // Deliberately narrow. Flagging every unfinished registration
              // makes the flag noise; these are the states where something has
              // actually gone wrong or is about to be missed.
              const moveOptions = (schedule ?? []).filter(
                (m) => !m.cancelledAt && m.id !== inv.meetingId,
              );

              const needsAttention =
                !inv.leadershipNotifiedAt
                || inv.paymentStatus === 'failed'
                || (inv.registeredToday && inv.conflictStatus === 'pending');

              return (
                <li
                  key={inv.id}
                  className="rounded-lg border border-gray-200 p-4"
                  data-testid="guest-review-row"
                  data-guest-email={inv?.prospectMember?.email ?? ''}
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900">
                        {inv?.prospectMember?.name || 'Guest'}
                        {inv.registeredToday ? (
                          <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900">
                            Registered today
                          </span>
                        ) : null}
                      </div>
                      <div className="text-sm text-gray-600">{inv?.prospectMember?.businessName || ''}</div>
                      <div className="text-sm text-gray-600">{inv?.prospectMember?.email || ''}</div>

                      <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                        <div className="flex gap-2">
                          <dt className="text-gray-500">Business type</dt>
                          <dd className="text-gray-900" data-testid="guest-industry">
                            {inv.industry || 'Not provided'}
                          </dd>
                        </div>
                        <div className="flex gap-2">
                          <dt className="text-gray-500">Route</dt>
                          <dd className="text-gray-900">
                            {inv.requestedVia === 'member_invite' ? 'Member invitation' : 'Self-invitation'}
                          </dd>
                        </div>
                        <div className="flex gap-2">
                          <dt className="text-gray-500">Payer</dt>
                          <dd className="text-gray-900" data-testid="guest-payer">
                            {PAYER_LABELS[inv.payer] ?? 'Not selected'}
                          </dd>
                        </div>
                        <div className="flex gap-2">
                          <dt className="text-gray-500">Payment</dt>
                          <dd data-testid="guest-payment-status">
                            <StatusPill kind={PAYMENT_PILLS[inv.paymentStatus] ?? 'neutral'}>
                              {PAYMENT_LABELS[inv.paymentStatus] ?? 'Not started'}
                            </StatusPill>
                          </dd>
                        </div>
                      </dl>

                      <p className="mt-2 text-xs text-gray-500" data-testid="guest-comms">
                        Leadership notified: {inv.leadershipNotifiedAt ? 'yes' : 'not yet'}
                        {' · '}
                        Welcome sent: {inv.welcomeSentAt ? 'yes' : 'not yet'}
                        {' · '}
                        Confirmation sent: {inv.confirmationSentAt ? 'yes' : 'not yet'}
                      </p>
                    </div>

                    <div className="flex shrink-0 flex-col items-start gap-2 lg:items-end">
                      <StatusPill kind={CONFLICT_PILLS[inv.conflictStatus] ?? 'neutral'}>
                        <span data-testid="guest-conflict-status">
                          {CONFLICT_LABELS[inv.conflictStatus] ?? 'Review pending'}
                        </span>
                      </StatusPill>

                      {inv.conflictReviewedAt ? (
                        <span className="text-xs text-gray-500" data-testid="guest-reviewed-at">
                          Reviewed {new Date(inv.conflictReviewedAt).toLocaleString()}
                          {inv.conflictReviewedByName ? ` by ${inv.conflictReviewedByName}` : ''}
                        </span>
                      ) : null}

                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-NBNA-primary"
                          data-testid="guest-mark-no-conflict"
                          disabled={busyInviteId === inv.id || inv.conflictStatus === 'no_conflict'}
                          onClick={() => onRecordConflict(inv, 'no_conflict')}
                        >
                          No Conflict
                        </button>
                        <button
                          type="button"
                          className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-NBNA-primary"
                          data-testid="guest-mark-conflict"
                          disabled={busyInviteId === inv.id || inv.conflictStatus === 'conflict'}
                          onClick={() => onRecordConflict(inv, 'conflict')}
                        >
                          Conflict
                        </button>
                        {moveOptions.length ? (
                          <select
                            className="input-field text-sm"
                            data-testid="guest-move-meeting"
                            aria-label="Move this guest to another meeting"
                            value=""
                            disabled={busyInviteId === inv.id}
                            onChange={(e) => onMoveGuest(inv, e.target.value)}
                          >
                            <option value="">Move to…</option>
                            {moveOptions.map((m) => (
                              <option key={m.id} value={m.id}>
                                {new Date(m.startsAt).toLocaleDateString(undefined, {
                                  weekday: 'short', month: 'short', day: 'numeric',
                                })}
                              </option>
                            ))}
                          </select>
                        ) : null}

                        <button
                          type="button"
                          className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-opacity-100"
                          data-testid="guest-request-fee"
                          disabled={
                            busyInviteId === inv.id
                            || inv.conflictStatus !== 'no_conflict'
                            || inv.paymentStatus === 'paid'
                          }
                          onClick={() => onRequestGuestFee(inv)}
                        >
                          {inv.paymentStatus === 'paid' ? 'Paid' : 'Send welcome & request $30'}
                        </button>
                      </div>

                      {needsAttention ? (
                        <span className="text-xs text-amber-800" data-testid="guest-needs-attention">
                          Needs attention
                        </span>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-3 text-gray-600">No guest registrations for the next meeting.</p>
        )}
      </section>

    </div>
  );
}
